import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./VideoShowcase.scss";

const getYoutubeId = (url) => {
  if (!url) return "";

  const patterns = [
    /youtube\.com\/embed\/([^?&/]+)/,
    /youtube\.com\/watch\?v=([^?&/]+)/,
    /youtu\.be\/([^?&/]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);

    if (match) {
      return match[1];
    }
  }

  return "";
};

export const VideoShowcase = ({
  eyebrow,
  title,
  accentTitle,
  description,
  videos,
  viewAllPath,
  sectionNumber = "01"
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);

  const activeVideo = videos[activeIndex];

  const scrollTrack = (direction) => {
    if (!trackRef.current) return;

    trackRef.current.scrollBy({
      left: direction === "next" ? 420 : -420,
      behavior: "smooth"
    });
  };

  return (
    <section className="video-showcase">
      <div className="video-showcase-inner">
        <div className="video-showcase-heading">
          <div>
            <span className="video-section-number">{sectionNumber}</span>

            <span className="video-eyebrow">{eyebrow}</span>
          </div>

          {viewAllPath && (
            <Link to={viewAllPath} className="video-view-all">
              VIEW ALL
              <span>↗</span>
            </Link>
          )}
        </div>

        <div className="video-showcase-intro">
          <h2>
            {title}
            <span>{accentTitle}</span>
          </h2>

          <p>{description}</p>
        </div>

        <div className="featured-video-wrapper">
          <iframe
            key={activeVideo}
            src={activeVideo}
            title={`${title} featured episode`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />

          <div className="featured-video-meta">
            <span>FEATURED EPISODE</span>

            <strong>EP. {String(activeIndex + 1).padStart(2, "0")}</strong>
          </div>
        </div>

        <div className="episodes-header">
          <div>
            <span>DISCOVER MORE</span>
            <h3>Latest episodes</h3>
          </div>

          <div className="episodes-controls">
            <button
              onClick={() => scrollTrack("prev")}
              aria-label="Previous episodes"
            >
              ←
            </button>

            <button
              onClick={() => scrollTrack("next")}
              aria-label="Next episodes"
            >
              →
            </button>
          </div>
        </div>

        <div className="episode-track" ref={trackRef}>
          {videos.map((video, index) => {
            const videoId = getYoutubeId(video);

            return (
              <button
                type="button"
                key={`${videoId}-${index}`}
                className={
                  index === activeIndex ? "episode-card active" : "episode-card"
                }
                onClick={() => setActiveIndex(index)}
              >
                <div className="episode-thumbnail">
                  <img
                    src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                    alt={`${title} episode ${index + 1}`}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                    }}
                  />

                  <div className="episode-overlay" />

                  <div className="episode-play">▶</div>

                  <span className="episode-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="episode-info">
                  <div>
                    <small>{eyebrow}</small>
                    <strong>
                      Episode {String(index + 1).padStart(2, "0")}
                    </strong>
                  </div>

                  <span>↗</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
